import { lazy, Suspense, useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowClockwise, CardsThree, Coins, Lifebuoy, Receipt, ShieldCheck, UsersThree, Warning } from "@phosphor-icons/react";
import {
  api,
  loadEffectAssetURL,
  mutationHeaders,
  type AuditEvent,
  type EffectAssetDescriptor,
  type EffectCardSummary,
  type EffectLabManifest,
  type EffectLabRecord,
  type Generation,
  type Overview,
  type PurchaseEvent,
  type SharedLinkRecord,
} from "./api";
import type { EffectRecipe, SceneAssetURLs } from "@lorefoil/web-renderer";

const EffectCardScene = lazy(async () => {
  const module = await import("@lorefoil/web-renderer");
  return { default: module.CardScene };
});

type Section = "overview" | "generations" | "effects" | "purchases" | "shares" | "support" | "audit";

const navigation: Array<{ id: Section; label: string }> = [
  { id: "overview", label: "Overview" },
  { id: "generations", label: "Generations" },
  { id: "effects", label: "Effect Lab" },
  { id: "purchases", label: "Purchases" },
  { id: "shares", label: "Shared links" },
  { id: "support", label: "Support" },
  { id: "audit", label: "Audit log" },
];

export function App() {
  const [section, setSection] = useState<Section>("overview");
  return (
    <div className="shell">
      <header className="topbar">
        <a className="brand" href="/" aria-label="Lorefoil Admin home"><img src="https://lorefoil.kifferliu.com/assets/brand-mark.png" alt="" width="36" height="36" /><strong>Lorefoil Admin</strong></a>
        <span className="access"><ShieldCheck size={18} weight="duotone" /> Protected by Access</span>
      </header>
      <div className="layout">
        <nav className="nav" aria-label="Admin sections">
          {navigation.map((item) => (
            <button key={item.id} className={section === item.id ? "active" : ""} onClick={() => setSection(item.id)}>{item.label}</button>
          ))}
        </nav>
        <main>
          {section === "overview" && <OverviewPanel />}
          {section === "generations" && <GenerationsPanel />}
          {section === "effects" && <EffectLabPanel />}
          {section === "purchases" && <PurchasesPanel />}
          {section === "shares" && <SharesPanel />}
          {section === "support" && <SupportPanel />}
          {section === "audit" && <AuditPanel />}
        </main>
      </div>
    </div>
  );
}

function OverviewPanel() {
  const query = useQuery({ queryKey: ["overview"], queryFn: () => api<Overview>("/overview") });
  if (query.isPending) return <PanelSkeleton title="Overview" />;
  if (query.isError) return <ErrorPanel title="Overview unavailable" message={query.error.message} retry={() => void query.refetch()} />;
  const value = query.data;
  const metrics = [
    { label: "Active users", value: value.users, icon: UsersThree },
    { label: "Cards", value: value.cards, icon: CardsThree },
    { label: "Active jobs", value: value.activeGenerations, icon: ArrowClockwise },
    { label: "Failed in 24h", value: value.failedGenerations24h, icon: Warning },
    { label: "Purchase issues", value: value.purchaseIssues, icon: Receipt },
    { label: "Open support", value: value.openSupportRequests, icon: Lifebuoy },
  ];
  return (
    <section>
      <Heading title="Overview" subtitle={`Operational snapshot as of ${formatDate(value.asOf)}`} />
      <div className="metric-grid">
        {metrics.map(({ label, value: amount, icon: Icon }) => (
          <article className="metric" key={label}><Icon size={22} weight="duotone" /><span>{label}</span><strong>{amount.toLocaleString()}</strong></article>
        ))}
      </div>
      <div className="notice"><Coins size={22} weight="duotone" /><div><strong>Provider cost, last 24 hours</strong>{value.providerCosts24h ? value.providerCosts24h.map((cost) => <p key={`${cost.provider}:${cost.unit}`}>{cost.provider}: {cost.amount === null ? "Cost not confirmed" : `${cost.amount.toLocaleString()} ${cost.unit}`}{cost.unpricedRequests > 0 ? ` · ${cost.unpricedRequests} requests awaiting cost data` : ""}</p>) : <p>{value.providerCost24h.amount.toLocaleString()} {value.providerCost24h.unit}</p>}</div></div>
    </section>
  );
}

function GenerationsPanel() {
  const client = useQueryClient();
  const [filter, setFilter] = useState("all");
  const query = useQuery({
    queryKey: ["generations", filter],
    queryFn: () => api<{ items: Generation[] }>(`/generations${filter === "all" ? "" : `?status=${encodeURIComponent(filter)}`}`),
  });
  const reconcile = useMutation({
    mutationFn: (id: string) => api<{ accepted: boolean }>(`/generations/${id}/reconcile`, { method: "POST", headers: mutationHeaders() }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["generations"] }),
  });
  return (
    <section>
      <Heading title="Generations" subtitle="Private inputs and provider URLs are intentionally hidden." />
      <div className="toolbar">
        <label>Status<select value={filter} onChange={(event) => setFilter(event.target.value)}><option value="all">All</option><option value="reconciling">Reconciling</option><option value="failed">Failed</option><option value="completed">Completed</option></select></label>
        <button className="secondary" onClick={() => void query.refetch()}><ArrowClockwise size={18} /> Refresh</button>
      </div>
      {query.isPending ? <TableSkeleton /> : query.isError ? <ErrorPanel title="Generations unavailable" message={query.error.message} retry={() => void query.refetch()} /> : (
        <DataTable headers={["Job", "Status", "Billing", "Provider", "Updated", "Action"]} empty={query.data.items.length === 0}>
          {query.data.items.map((job) => <tr key={job.id}><td><code>{shortID(job.id)}</code><small>{shortID(job.ownerId)}</small></td><td><Status value={job.status} /><small>{job.errorCode ?? "No error"}</small></td><td>{job.billingStatus}<small>{job.quotedCost} credits</small></td><td>{job.provider ?? "Not submitted"}<small>{job.providerState ?? "No attempt"}</small></td><td>{formatDate(job.updatedAt)}</td><td>{["queued", "reconciling"].includes(job.status) ? <button className="text-button" disabled={reconcile.isPending} onClick={() => reconcile.mutate(job.id)}>Reconcile</button> : <span className="muted">No action</span>}</td></tr>)}
        </DataTable>
      )}
      {reconcile.isError && <InlineError message={reconcile.error.message} />}
    </section>
  );
}

function EffectLabPanel() {
  const [lookup, setLookup] = useState("");
  const [cardId, setCardId] = useState<string>();
  const [lookupError, setLookupError] = useState<string>();
  const [recipe, setRecipe] = useState<EffectRecipe>();
  const [instruction, setInstruction] = useState("");
  const [reason, setReason] = useState("");
  const query = useQuery({
    queryKey: ["effect-lab", cardId],
    queryFn: () => api<EffectLabRecord>(`/cards/${cardId}/effect`),
    enabled: cardId !== undefined,
  });
  const recentCards = useQuery({
    queryKey: ["effect-cards"],
    queryFn: () => api<{ items: EffectCardSummary[] }>("/effect-cards?limit=12"),
  });
  const revision = useMutation({
    mutationFn: (request: { path: string; body: Record<string, unknown> }) =>
      api<{ revisionId: string; cardVersion: number; warnings: string[] }>(request.path, {
        method: "POST",
        headers: mutationHeaders(),
        body: JSON.stringify(request.body),
      }),
    onSuccess: () => {
      setInstruction("");
      setReason("");
      void query.refetch();
    },
  });

  useEffect(() => {
    if (query.data) setRecipe(structuredClone(query.data.manifest.effectRecipe));
  }, [query.data]);

  function submitLookup(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalized = lookup.trim();
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(normalized)) {
      setLookupError("Enter a complete card UUID.");
      return;
    }
    setLookupError(undefined);
    revision.reset();
    setCardId(normalized);
  }

  function openCard(id: string) {
    setLookup(id);
    setLookupError(undefined);
    revision.reset();
    setCardId(id);
  }

  const data = query.data;
  const warnings = recipe ? effectPreviewWarnings(recipe) : [];
  return <section>
    <Heading title="Effect Lab" subtitle="Direct one card’s optical performance without changing its artwork or exposing private prompts." />
    <form className="effect-lookup" onSubmit={submitLookup}>
      <label>Card UUID<input value={lookup} onChange={(event) => setLookup(event.target.value)} placeholder="00000000-0000-0000-0000-000000000000" autoComplete="off" /></label>
      <button className="primary" type="submit">Open card</button>
    </form>
    {recentCards.data?.items.length ? <div className="effect-recent" aria-label="Recent generative cards">
      <span>Recent v2 cards</span>
      <div>{recentCards.data.items.map((card) => <button
        className={card.id === cardId ? "active" : ""}
        type="button"
        key={card.id}
        onClick={() => openCard(card.id)}
      ><strong>{card.title}</strong><small>Revision {card.revisionSequence} · {formatDate(card.updatedAt)}</small></button>)}</div>
    </div> : null}
    {lookupError && <InlineError message={lookupError} />}
    {cardId && query.isPending && <PanelSkeleton title="Loading Effect Lab" />}
    {query.isError && <ErrorPanel title="Effect Lab unavailable" message={query.error.message} retry={() => void query.refetch()} />}
    {data && recipe && <div className="effect-lab-grid">
      <div className="effect-preview-column">
        <EffectPreview
          cardId={data.card.id}
          title={data.card.title}
          manifest={{ ...data.manifest, effectRecipe: recipe }}
        />
        <div className={`effect-health ${warnings.length ? "has-warnings" : ""}`}>
          <strong>{warnings.length ? `${warnings.length} preview warning${warnings.length === 1 ? "" : "s"}` : "Within runtime budget"}</strong>
          {warnings.length ? <ul>{warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul> : <p>No performance or readability warning from the bounded recipe.</p>}
        </div>
      </div>
      <div className="effect-controls">
        <div className="effect-card-heading"><div><span>Editing</span><h2>{data.card.title}</h2></div><code>v{data.card.version} · {shortID(data.revisionId)}</code></div>
        <fieldset disabled={revision.isPending}>
          <legend>Live bounded controls</legend>
          <RangeControl label="Subject scale" value={recipe.composition.subjectScale} min={0.8} max={1.6} step={0.01} onChange={(value) => setRecipe({ ...recipe, composition: { ...recipe.composition, subjectScale: value } })} />
          <RangeControl label="Foil strength" value={recipe.foil.intensity} min={0} max={1} step={0.01} onChange={(value) => setRecipe({ ...recipe, foil: { ...recipe.foil, intensity: value } })} />
          <RangeControl label="Foil frequency" value={recipe.foil.frequency} min={0.2} max={24} step={0.1} onChange={(value) => setRecipe({ ...recipe, foil: { ...recipe.foil, frequency: value } })} />
          <RangeControl label="Contour glow" value={recipe.glow.intensity} min={0} max={1} step={0.01} onChange={(value) => setRecipe({ ...recipe, glow: { ...recipe.glow, intensity: value } })} />
          <RangeControl label="Bloom" value={recipe.post.bloom} min={0} max={0.65} step={0.01} onChange={(value) => setRecipe({ ...recipe, post: { ...recipe.post, bloom: value } })} />
          {recipe.emitters.map((emitter, index) => <RangeControl
            key={`${emitter.source}-${index}`}
            label={`Emitter ${index + 1} · ${emitter.shape}`}
            value={emitter.density}
            min={0}
            max={1}
            step={0.01}
            onChange={(value) => setRecipe({
              ...recipe,
              emitters: recipe.emitters.map((item, itemIndex) => itemIndex === index ? { ...item, density: value } : item),
            })}
          />)}
        </fieldset>
        <label className="effect-reason">Audit reason<input value={reason} onChange={(event) => setReason(event.target.value)} minLength={3} maxLength={240} placeholder="Why this revision is needed" /></label>
        <div className="effect-publish-row">
          <button className="primary" disabled={revision.isPending || reason.trim().length < 3} onClick={() => revision.mutate({
            path: `/cards/${data.card.id}/effect/revise`,
            body: { expectedCardVersion: data.card.version, recipe, reason: reason.trim() },
          })}>Publish controls as revision</button>
          <button className="secondary" disabled={revision.isPending} onClick={() => setRecipe(structuredClone(data.manifest.effectRecipe))}>Reset preview</button>
        </div>
        <div className="director-box">
          <label>Natural-language direction<textarea value={instruction} onChange={(event) => setInstruction(event.target.value)} minLength={3} maxLength={1200} placeholder="Make the particles feel like slow snow orbiting behind the subject; preserve facial readability." /></label>
          <button className="secondary" disabled={revision.isPending || instruction.trim().length < 3 || reason.trim().length < 3} onClick={() => revision.mutate({
            path: `/cards/${data.card.id}/effect/revise`,
            body: { expectedCardVersion: data.card.version, instruction: instruction.trim(), reason: reason.trim() },
          })}>Re-direct with AI</button>
        </div>
        {revision.isError && <InlineError message={revision.error.message} />}
        <div className="effect-history">
          <h3>Revision history</h3>
          {data.history.map((item) => <div className="effect-history-row" key={item.id}>
            <div><strong>Revision {item.sequence}</strong><small>{formatDate(item.createdAt)}{item.isCurrent ? " · Current" : ""}</small></div>
            {!item.isCurrent && <button className="text-button" disabled={revision.isPending || reason.trim().length < 3} onClick={() => revision.mutate({
              path: `/cards/${data.card.id}/effect/rollback`,
              body: { expectedCardVersion: data.card.version, targetRevisionId: item.id, reason: reason.trim() },
            })}>Restore recipe</button>}
          </div>)}
        </div>
      </div>
    </div>}
  </section>;
}

function EffectPreview({ cardId, title, manifest }: { cardId: string; title: string; manifest: EffectLabManifest }) {
  const [assets, setAssets] = useState<SceneAssetURLs>();
  const [error, setError] = useState<string>();
  const [isFlipped, setIsFlipped] = useState(false);
  const [webGLFailed, setWebGLFailed] = useState(false);
  const onWebGLUnavailable = useCallback(() => setWebGLFailed(true), []);

  useEffect(() => {
    const controller = new AbortController();
    let disposed = false;
    let urls: string[] = [];
    setAssets(undefined);
    setError(undefined);
    setWebGLFailed(false);
    const descriptors: Array<[keyof SceneAssetURLs, EffectAssetDescriptor]> = [
      ["front", manifest.front.preview],
      ["back", manifest.back.preview],
      ["background", manifest.front.background],
      ["subject", manifest.front.subject],
      ["lineart", manifest.front.lineart],
      ["typography", manifest.front.typography],
    ];
    if (manifest.front.foilMask) descriptors.push(["foilMask", manifest.front.foilMask]);
    if (manifest.front.exclusionMask) descriptors.push(["exclusionMask", manifest.front.exclusionMask]);
    if (manifest.front.particleAtlas) descriptors.push(["particleAtlas", manifest.front.particleAtlas]);
    Promise.all(descriptors.map(async ([key, descriptor]) => [key, await loadEffectAssetURL(cardId, descriptor, controller.signal)] as const))
      .then((loaded) => {
        urls = loaded.map(([, url]) => url);
        if (disposed) {
          urls.forEach(URL.revokeObjectURL);
          urls = [];
          return;
        }
        setAssets(Object.fromEntries(loaded) as unknown as SceneAssetURLs);
      })
      .catch(() => { if (!disposed) setError("The verified rendering layers could not be loaded."); });
    return () => {
      disposed = true;
      controller.abort();
      urls.forEach(URL.revokeObjectURL);
    };
  }, [cardId, manifest.back.preview.assetId, manifest.front.background.assetId, manifest.front.exclusionMask?.assetId,
    manifest.front.foilMask?.assetId, manifest.front.lineart.assetId, manifest.front.particleAtlas?.assetId,
    manifest.front.preview.assetId, manifest.front.subject.assetId, manifest.front.typography.assetId]);

  return <div className="effect-preview-shell">
    <div className="effect-stage">
      {!assets && !error && <div className="effect-loading">Loading verified layers…</div>}
      {error && <div className="effect-loading danger-text">{error}</div>}
      {assets && (webGLFailed
        ? <img src={isFlipped ? assets.back : assets.front} alt={`${isFlipped ? "Back" : "Front"} of ${title}`} />
        : <Suspense fallback={<img src={assets.front} alt={`Front of ${title}`} />}>
          <EffectCardScene assets={assets} manifest={manifest} isFlipped={isFlipped} onWebGLUnavailable={onWebGLUnavailable} />
        </Suspense>)}
    </div>
    <button className="secondary" onClick={() => setIsFlipped((value) => !value)}>{isFlipped ? "Show front" : "Flip card"}</button>
  </div>;
}

function RangeControl({ label, value, min, max, step, onChange }: { label: string; value: number; min: number; max: number; step: number; onChange: (value: number) => void }) {
  return <label className="range-control"><span>{label}<output>{value.toFixed(step < 0.1 ? 2 : 1)}</output></span><input type="range" value={value} min={min} max={max} step={step} onChange={(event) => onChange(Number(event.target.value))} /></label>;
}

function effectPreviewWarnings(recipe: EffectRecipe): string[] {
  const warnings: string[] = [];
  if (recipe.emitters.reduce((sum, emitter) => sum + emitter.density, 0) > 1.6) warnings.push("High combined particle density may reduce frame stability.");
  if (recipe.emitters.length === 3) warnings.push("Three emitters use the full particle budget.");
  if (recipe.post.targetFPS === 120) warnings.push("120 fps remains opportunistic under thermal pressure.");
  if (recipe.post.bloom > 0.5) warnings.push("Strong bloom can soften small details.");
  if (recipe.foil.intensity > 0.85 && recipe.glow.intensity > 0.75) warnings.push("Foil and glow may compete with subject readability.");
  return warnings;
}

function PurchasesPanel() {
  const [filter, setFilter] = useState("all");
  const query = useQuery({ queryKey: ["purchases", filter], queryFn: () => api<{ items: PurchaseEvent[] }>(`/purchases${filter === "all" ? "" : `?status=${encodeURIComponent(filter)}`}`) });
  return (
    <section>
      <Heading title="Purchases" subtitle="Webhook lifecycle events. Customer payment details are not stored here." />
      <div className="toolbar"><label>Status<select value={filter} onChange={(event) => setFilter(event.target.value)}><option value="all">All</option><option value="reconciling">Reconciling</option><option value="rejected">Rejected</option><option value="applied">Applied</option></select></label><CompensationForm /></div>
      {query.isPending ? <TableSkeleton /> : query.isError ? <ErrorPanel title="Purchases unavailable" message={query.error.message} retry={() => void query.refetch()} /> : (
        <DataTable headers={["Event", "Product", "Type", "Status", "Received"]} empty={query.data.items.length === 0}>
          {query.data.items.map((event) => <tr key={event.eventId}><td><code>{shortID(event.eventId)}</code><small>{event.ownerId ? shortID(event.ownerId) : "Unmatched user"}</small></td><td>{event.productId}</td><td>{event.type}</td><td><Status value={event.status} /><small>{event.errorCode ?? "No error"}</small></td><td>{formatDate(event.receivedAt)}</td></tr>)}
        </DataTable>
      )}
    </section>
  );
}

function SharesPanel() {
  const client = useQueryClient();
  const [filter, setFilter] = useState("active");
  const [pending, setPending] = useState<SharedLinkRecord>();
  const query = useQuery({
    queryKey: ["shares", filter],
    queryFn: () => api<{ items: SharedLinkRecord[] }>(`/shares${filter === "all" ? "" : `?status=${encodeURIComponent(filter)}`}`),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api<{ revoked: boolean }>(`/shares/${encodeURIComponent(id)}/revoke`, { method: "POST", headers: mutationHeaders() }),
    onSuccess: () => {
      setPending(undefined);
      void client.invalidateQueries({ queryKey: ["shares"] });
    },
  });
  return (
    <section>
      <Heading title="Shared links" subtitle="Tokens are never shown. Revoking a link does not delete its card." />
      <div className="toolbar">
        <label>Status<select value={filter} onChange={(event) => setFilter(event.target.value)}><option value="active">Active</option><option value="revoked">Revoked</option><option value="all">All</option></select></label>
        <button className="secondary" onClick={() => void query.refetch()}><ArrowClockwise size={18} /> Refresh</button>
      </div>
      {query.isPending ? <TableSkeleton /> : query.isError ? <ErrorPanel title="Shared links unavailable" message={query.error.message} retry={() => void query.refetch()} /> : (
        <DataTable headers={["Card", "Share", "Status", "Created", "Action"]} empty={query.data.items.length === 0}>
          {query.data.items.map((share) => <tr key={share.id}><td>{share.cardTitle}<small><code>{shortID(share.cardId)}</code></small></td><td><code>{shortID(share.id)}</code><small>Owner {shortID(share.ownerId)}</small></td><td><Status value={share.status} />{share.revokedAt && <small>{formatDate(share.revokedAt)}</small>}</td><td>{formatDate(share.createdAt)}</td><td>{share.status === "active" ? <button className="text-button danger-text" onClick={() => { revoke.reset(); setPending(share); }}>Revoke</button> : <span className="muted">No action</span>}</td></tr>)}
        </DataTable>
      )}
      {pending && <RevokeShareDialog share={pending} busy={revoke.isPending} error={revoke.isError ? revoke.error.message : undefined} onCancel={() => setPending(undefined)} onConfirm={() => revoke.mutate(pending.id)} />}
    </section>
  );
}

function RevokeShareDialog({ share, busy, error, onCancel, onConfirm }: { share: SharedLinkRecord; busy: boolean; error?: string; onCancel: () => void; onConfirm: () => void }) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
    return () => { if (dialog?.open) dialog.close(); };
  }, []);
  return <dialog ref={dialogRef} className="confirm-dialog" aria-labelledby="revoke-title" aria-describedby="revoke-detail" onCancel={(event) => { event.preventDefault(); if (!busy) onCancel(); }}><h2 id="revoke-title">Revoke this shared link?</h2><p id="revoke-detail">Anyone using the link for <strong>{share.cardTitle}</strong> will immediately lose access. The card itself stays in its owner’s collection.</p>{error && <InlineError message={error} />}<div className="dialog-actions"><button className="secondary" autoFocus disabled={busy} onClick={onCancel}>Cancel</button><button className="danger-button" disabled={busy} onClick={onConfirm}>{busy ? "Revoking…" : "Revoke link"}</button></div></dialog>;
}

function CompensationForm() {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<string>();
  const mutation = useMutation({ mutationFn: (body: { ownerId: string; amount: number; reason: string }) => api<{ amount: number }>("/credits/compensations", { method: "POST", headers: mutationHeaders(), body: JSON.stringify(body) }) });
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    mutation.mutate({ ownerId: String(data.get("ownerId")), amount: Number(data.get("amount")), reason: String(data.get("reason")) }, { onSuccess: (value) => setResult(`${value.amount} credits granted.`) });
  }
  if (!open) return <button className="primary" onClick={() => setOpen(true)}><Coins size={18} /> Grant compensation</button>;
  return <form className="compensation" onSubmit={submit}><input name="ownerId" required placeholder="User UUID" aria-label="User UUID" /><input name="amount" required type="number" min="1" max="10000" placeholder="Credits" aria-label="Credit amount" /><input name="reason" required minLength={3} maxLength={240} placeholder="Support reason" aria-label="Compensation reason" /><button className="primary" disabled={mutation.isPending}>Grant</button><button type="button" className="secondary" onClick={() => setOpen(false)}>Cancel</button>{result && <span className="success">{result}</span>}{mutation.isError && <InlineError message={mutation.error.message} />}</form>;
}

function SupportPanel() {
  const query = useQuery({ queryKey: ["support"], queryFn: () => api<{ items: Array<Record<string, unknown>> }>("/support") });
  return <section><Heading title="Support" subtitle="Requests submitted through Lorefoil support channels." />{query.isPending ? <TableSkeleton /> : query.isError ? <ErrorPanel title="Support unavailable" message={query.error.message} retry={() => void query.refetch()} /> : <DataTable headers={["Request", "Category", "Message", "Status"]} empty={query.data.items.length === 0}>{query.data.items.map((item) => <tr key={String(item.id)}><td><code>{shortID(String(item.id))}</code><small>{String(item.email ?? item.owner_id ?? "No contact")}</small></td><td>{String(item.category)}</td><td className="message-cell">{String(item.message)}</td><td><Status value={String(item.status)} /></td></tr>)}</DataTable>}</section>;
}

function AuditPanel() {
  const query = useQuery({ queryKey: ["audit"], queryFn: () => api<{ items: AuditEvent[] }>("/audit") });
  return <section><Heading title="Audit log" subtitle="Every privileged mutation records the actor, target and reason." />{query.isPending ? <TableSkeleton /> : query.isError ? <ErrorPanel title="Audit log unavailable" message={query.error.message} retry={() => void query.refetch()} /> : <DataTable headers={["Action", "Target", "Actor", "Reason", "Time"]} empty={query.data.items.length === 0}>{query.data.items.map((item) => <tr key={item.id}><td>{item.action}</td><td><code>{shortID(item.targetId)}</code><small>{item.targetType}</small></td><td>{item.actorEmail}</td><td>{item.reason}</td><td>{formatDate(item.createdAt)}</td></tr>)}</DataTable>}</section>;
}

function Heading({ title, subtitle }: { title: string; subtitle: string }) { return <header className="heading"><h1>{title}</h1><p>{subtitle}</p></header>; }
function Status({ value }: { value: string }) { return <span className={`status status-${value.toLowerCase()}`}>{value}</span>; }
function InlineError({ message }: { message: string }) { return <p className="inline-error" role="alert">{message}</p>; }
function ErrorPanel({ title, message, retry }: { title: string; message: string; retry: () => void }) { return <div className="error-panel" role="alert"><Warning size={24} /><div><strong>{title}</strong><p>{message}</p></div><button className="secondary" onClick={retry}>Try again</button></div>; }
function PanelSkeleton({ title }: { title: string }) { return <section><Heading title={title} subtitle="Loading current operational data." /><div className="metric-grid loading" aria-label="Loading"><i /><i /><i /><i /></div></section>; }
function TableSkeleton() { return <div className="table-skeleton" aria-label="Loading"><i /><i /><i /><i /></div>; }
function DataTable({ headers, empty, children }: { headers: string[]; empty: boolean; children: ReactNode }) { return <div className="table-wrap">{empty ? <div className="empty">No matching records.</div> : <table><thead><tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr></thead><tbody>{children}</tbody></table>}</div>; }
function shortID(value: string) { return value.length > 12 ? `${value.slice(0, 8)}…` : value; }
function formatDate(value: string) { return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)); }
